import { LinerLocation, PutLinerLocation } from "features/organizations/asset-sections/models/asset-section-models"
import { CssSVGClasses } from "../editor-utils/editor-types"
import { ExtractDefinedLinerNumber, GetLinerId, GetLinersId } from "../editor-utils/id-string-utils"
import { IsHoleInsideLiner } from "./holes-helper"
import { RemoveUnListedClasses } from "./svg-helpers"
import { GetLinerLocationInfo } from "./wear-reference-plane-helper"

export function MapLiners(
    linerLocations: LinerLocation[],
    svgDocument: Document,
    renderedSvg: SVGSVGElement,
    selectedLiners: PutLinerLocation[],
    setSelectedLiners: (liners: PutLinerLocation[]) => void,
    strokeWidth: number,
): void {
    try {
        const originalAllPath = Array.from(svgDocument.querySelectorAll<SVGPathElement>("path"))
        const originalLiners = originalAllPath.filter(path => path.id.toLowerCase().startsWith("liners"))
        const renderedPaths = Array.from(renderedSvg.querySelectorAll<SVGPathElement>("path"))
        const renderedliners = renderedPaths
            .filter(path => path.id.toLowerCase().startsWith("liners"))
            .sort((a, b) => {
                const numA = parseInt(a.id.toLowerCase().split("_")[1], 10)
                const numB = parseInt(b.id.toLowerCase().split("_")[1], 10)
                return numA - numB
            })

        // the SVG might have some defined liners such as LinerA8, so it will take the last number 8 to be increased on top of it.
        let topDefinedLinerCounter: number = GetTopDefinedLinerCount(svgDocument)

        let updatedLiners = [...selectedLiners]
        renderedliners.forEach((liner: SVGPathElement) => {
            topDefinedLinerCounter++
            const linerIdPart = liner.id
            const isDefinedLiner: boolean = liner.classList.contains(CssSVGClasses.WsLiner)
            if (linerIdPart) {
                // if the path is a defined liner then get its as it is.
                const existLinerId = isDefinedLiner ? GetLinerId(linerIdPart) : GetLinersId(linerIdPart) // the defined liner should start with "Liner", while the not defined should start with "Liners".
                const linerLocationInfo = GetLinerLocationInfo(linerLocations, existLinerId as string)
                const linerNumber = topDefinedLinerCounter
                const newId = `LinerA${linerNumber}` // adjust this ID format as per your needs
                if (existLinerId) {
                    const linerToEdit = originalLiners.find(l => {
                        return l.id.toLowerCase() === existLinerId.toLowerCase()
                    })
                    if (linerToEdit) {
                        RemoveUnListedClasses(linerToEdit)
                        SetLinerAtributes(linerToEdit, newId, strokeWidth)
                        linerToEdit.id = newId
                        linerToEdit.classList.add(CssSVGClasses.WsLiner)
                        linerToEdit.setAttribute("stroke-width", strokeWidth.toString())
                        linerToEdit.setAttribute("stroke", "black")

                        const existingLinerIndex = updatedLiners.findIndex(entry => entry.existReference === existLinerId)
                        if (existingLinerIndex !== -1) {
                            updatedLiners[existingLinerIndex].newReference = newId
                        } else {
                            updatedLiners.push({ id: linerLocationInfo?.id ?? undefined, existReference: existLinerId, newReference: newId })
                        }
                    }
                }
            }
        })
        setSelectedLiners(updatedLiners)
    } catch (e) {
        console.error("Error happen in MapLiners", e)
    }
}

/**
 * Retrieves the ID of the liner surrounding a specified hole within an SVG element.
 *
 * This function searches for paths in the SVG that represent liners. It then checks if the
 * specified hole is inside any of these liners. If a surrounding liner is found, the function
 * returns its ID; otherwise, it returns null.
 *
 * Note: The function uses specific checks to filter out paths that are not liners by looking for
 * the presence of the term 'liner' in their IDs and ensuring they don't include 'hole' or 'region'.
 *
 * @param {SVGPathElement} hole - The SVG path element representing the hole.
 * @param {SVGSVGElement} svg - The SVG element containing paths.
 *
 * @returns {string | null} - The ID of the surrounding liner or null if no liner surrounds the hole.
 */
export function GetSurroundingLinerID(hole: SVGPathElement, svg: SVGSVGElement): string | null {
    try {
        if (!svg) return null
        const paths = Array.from(svg.querySelectorAll("path")) as SVGPathElement[]
        // filter the paths from regions and holes.
        const liners = paths.filter(path => path.classList.contains(CssSVGClasses.WsLiner))

        for (let liner of liners) {
            if (IsHoleInsideLiner(hole, liner)) {
                return GetLinerId(liner.id)
            }
        }
        return null
    } catch (e) {
        console.error("Error happen in GetSurroundingLinerID", e)
        return null
    }
}

/**
 * Sets various attributes for an SVG path element that represents a liner.
 *
 * This function is designed to modify an SVG path element (referred to as 'liner') by setting specific
 * attributes on it. These attributes include stroke width, an ID, and CSS class names relevant to its
 * role as a liner in the SVG context. The function also sets the stroke color to black.
 *
 * @param {SVGPathElement} liner - The SVG path element to which attributes will be applied.
 * @param {string} id - The ID to be assigned to the liner.
 * @param {number} strokeWidth - The stroke width to be applied to the liner.
 *
 * The function performs the following actions:
 * 1. Checks if the liner already has a 'stroke-width' attribute set and if it's not zero. If not,
 *    it sets the 'stroke-width' attribute to the provided strokeWidth value.
 * 2. Sets the 'id' attribute of the liner to the provided id.
 * 3. Adds the class 'WsLiner' (from CssSVGClasses) to the liner.
 * 4. Sets the 'stroke' attribute to 'black'.
 *
 * @returns {void} This function does not return a value; it modifies the liner element directly.
 *
 * Note: The function includes error handling, which logs any errors to the console. It ensures that
 *       any unexpected issues during the attribute setting process are caught and logged.
 */
export function SetLinerAtributes(liner: SVGPathElement, id: string, strokeWidth: number): void {
    try {
        const existStrokeWidth = liner.getAttribute("stroke-width")
        if (!existStrokeWidth || parseFloat(existStrokeWidth) === 0) {
            liner.setAttribute("stroke-width", strokeWidth.toString())
        }
        liner.id = id
        liner.classList.add(CssSVGClasses.WsLiner)
        liner.setAttribute("stroke", "black")
    } catch (e) {
        console.error("Error happen in SetLinerAtributes", e)
    }
}

export function GetTopDefinedLinerCount(svgDocument: Document): number {
    try {
        const originalAllPath = Array.from(svgDocument.querySelectorAll<SVGPathElement>("path"))
        const definedLiners = originalAllPath.filter(
            path => path.id.toLowerCase().startsWith("liner") && path.classList.contains(CssSVGClasses.WsLiner),
        )

        let topCount = 0
        if (definedLiners.length > 0) {
            definedLiners.forEach(d => {
                const excractedNumber = ExtractDefinedLinerNumber(d.id)
                if (excractedNumber !== null && excractedNumber > topCount) topCount = excractedNumber
            })
        }
        return topCount
    } catch (e) {
        console.error("Error in GetTopDefinedLinerCount", e)
        return 0
    }
}
